import { FC, useState } from 'react';
import { MenuItemInterface, MenuSubItemInterface } from '../../types';
import MenuSubItem from '../MenuSubItem';
import { useStore } from '../../../../../../hooks/useStore';
import { useQuery } from '@tanstack/react-query';
import api from '@api-client/client';
import {
  Avatar,
  ChevronDownIcon,
  Text
} from 'stubben-edge-unified-frontend-react-components';
import { useCompanyMenuLetterAvatarProps } from '@/utils/useAvatarPresentation';
import { useSidebarCollapsed } from '../../../Sidebar/SidebarCollapsedContext';
import SidebarFlyout from '../../../Sidebar/SidebarFlyout';

const CompanyMenuItem: FC<MenuItemInterface> = ({ subItems }) => {
  const { collapsed } = useSidebarCollapsed();
  const [isSubMenuActive, setIsSubMenuActive] = useState(false);
  const { authStore } = useStore();
  const { user, hasPermission } = authStore;
  const company = user?.company;
  const companyId = company?.id;
  const { data: { company_branding } = {} } = useQuery({
    queryKey: ['companyProfile', companyId],
    queryFn: () => api.company.getAllCompanyData(companyId),
    enabled: !!companyId
  });
  const companyLetterAvatar = useCompanyMenuLetterAvatarProps(
    company_branding,
    company
  );
  const avatarImage =
    (typeof company_branding?.company_icon === 'string' &&
      company_branding.company_icon) ||
    (typeof company?.company_icon === 'string' && company.company_icon) ||
    undefined;

  const avatar = (
    <Avatar
      size="sm"
      image={avatarImage}
      variant={avatarImage ? 'image' : 'letter'}
      label={company?.name ?? ''}
      showTooltip={false}
      {...companyLetterAvatar}
      testIdentifier="layout-company-menu-brand"
    />
  );

  const subItemList = subItems?.map(
    (subMenuItem: MenuSubItemInterface, index) =>
      (!subMenuItem.rules || hasPermission(subMenuItem.rules)) && (
        <MenuSubItem {...subMenuItem} key={index} />
      )
  );

  if (collapsed) {
    return (
      <div className="py-2">
        <SidebarFlyout
          buttonContent={avatar}
          buttonClassName="flex h-12 w-full items-center justify-center hover:bg-gray-50"
          buttonTestId="sidebar-nav-company-switcher"
          buttonAriaLabel={company?.name}
          panelTestId="sidebar-nav-company-flyout"
        >
          <div className="flex flex-col gap-1">{subItemList}</div>
        </SidebarFlyout>
      </div>
    );
  }

  return (
    <div className="p-6">
      <button
        type="button"
        data-testid="sidebar-nav-company-switcher"
        onClick={() => setIsSubMenuActive(!isSubMenuActive)}
        className={`flex w-full items-center gap-2 rounded-lg border border-gray-300 px-4 py-3 text-left hover:bg-gray-50 ${
          isSubMenuActive ? 'border border-primary' : ''
        }`}
      >
        <div className="shrink-0">{avatar}</div>
        <div className="flex-1">
          <Text
            size="xs"
            bold="heavy"
            variant="secondary"
            mode={isSubMenuActive ? 'brand' : 'normal'}
            testIdentifier="sidebar-nav-company-name"
          >
            {company?.name}
          </Text>
        </div>
        <div className="shrink-0">
          <ChevronDownIcon
            size={18}
            color={isSubMenuActive ? '#106df6' : '#667085'}
            className={`transition-transform duration-200 ease-in-out ${
              isSubMenuActive ? 'rotate-180' : ''
            }`}
          />
        </div>
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
          subItems && isSubMenuActive ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
        aria-hidden={!isSubMenuActive}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="relative mx-8 my-4 flex flex-col gap-2 pl-0.5">
            <span
              aria-hidden
              className={`pointer-events-none absolute bottom-3 left-0 top-0 w-0.5 ease-in-out transition-[opacity,background-color] [transition-duration:700ms,1400ms] ${
                isSubMenuActive
                  ? 'bg-gray-200 opacity-100'
                  : 'bg-primary opacity-0'
              }`}
            />
            {subItemList}
          </div>
        </div>
      </div>
    </div>
  );
};
export default CompanyMenuItem;
